import React, { useState, useEffect } from "react";
import "./VendorDashboard.css";
import API_URL from "../apiConfig";

const API_BASE_URL = API_URL;

const DEFAULT_CATEGORIES = [
  "Kurti",
  "Lehenga",
  "Sarees",
  "Palazo sets",
  "Blouses",
  "Ethnic",
].map((name, index) => ({ _id: `default-${index}`, name }));

const VendorDashboard = ({ onLogout }) => {
  const [products, setProducts] = useState([]);
  const [showUpload, setShowUpload] = useState(false);
  const [activeTab, setActiveTab] = useState("products"); // 'products', 'blouseBases', 'workComponents', 'references'
  const [blouseBases, setBlouseBases] = useState([]);
  const [workComponents, setWorkComponents] = useState([]);
  const [references, setReferences] = useState([]);
  const [categories, setCategories] = useState(DEFAULT_CATEGORIES);
  const [newCategoryName, setNewCategoryName] = useState("");
  const [showAddCategory, setShowAddCategory] = useState(false);
  const [selectedMappingBlouse, setSelectedMappingBlouse] = useState(null);
  const [loading, setLoading] = useState(true);
  const [formData, setFormData] = useState({
    name: "",
    brand: "",
    price: "",
    subCategory: "",
    description: "",
    sizes: [],
    colors: "",
    fabric: "",
    images: [], // Changed from image to images array
  });
  const [previews, setPreviews] = useState([]);

  const availableSizes = ["S", "M", "L", "XL", "XXL", "Free Size"];

  useEffect(() => {
    fetchProducts();
    fetchCustomizationData();
    fetchCategories();
  }, []);

  const fetchCategories = async () => {
    try {
      const res = await fetch(`${API_BASE_URL}/api/categories`);
      if (res.ok) {
        const data = await res.json();
        setCategories(data);
        // Set default category ONLY if current subCategory is empty and categories exist
        setFormData((prev) => {
          if (prev.subCategory || data.length === 0) return prev;
          return { ...prev, subCategory: data[0].name };
        });
      }
    } catch (err) {
      console.error("Error fetching categories:", err);
    }
  };

  const handleAddCategory = async () => {
    if (!newCategoryName.trim()) return;
    try {
      const res = await fetch(`${API_BASE_URL}/api/categories`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ name: newCategoryName }),
      });
      if (res.ok) {
        const newlyCreated = await res.json();
        setFormData((prev) => ({ ...prev, subCategory: newlyCreated.name }));
        setNewCategoryName("");
        setShowAddCategory(false);
        fetchCategories();
      } else {
        const err = await res.json();
        alert(err.message || "Failed to add category");
      }
    } catch (err) {
      console.error("Error adding category:", err);
    }
  };

  const fetchCustomizationData = async () => {
    try {
      const userStr = localStorage.getItem("user");
      if (!userStr) return;
      const user = JSON.parse(userStr);

      const [basesRes, componentsRes, refsRes] = await Promise.all([
        fetch(`${API_BASE_URL}/api/customization/bases?vendorId=${user.id}`),
        fetch(
          `${API_BASE_URL}/api/customization/components?vendorId=${user.id}`,
        ),
        fetch(
          `${API_BASE_URL}/api/customization/references?vendorId=${user.id}`,
        ),
      ]);

      const [bases, components, refs] = await Promise.all([
        basesRes.json(),
        componentsRes.json(),
        refsRes.json(),
      ]);

      setBlouseBases(bases);
      setWorkComponents(components);
      setReferences(refs);
    } catch (err) {
      console.error("Error fetching customization data:", err);
    }
  };

  const fetchProducts = async () => {
    try {
      // get current user from local storage
      const userStr = localStorage.getItem("user");
      if (!userStr) return;

      const user = JSON.parse(userStr);

      if (!user || !user.id) {
        console.error("No valid user ID found in local storage");
        return;
      }

      // Fetch only products created by this vendor
      const res = await fetch(
        `${API_BASE_URL}/api/products?vendorId=${user.id}`,
      );
      if (!res.ok) {
        const errData = await res.json();
        throw new Error(errData.message || "Failed to fetch");
      }
      const data = await res.json();
      setProducts(data);
    } catch (err) {
      console.error(err);
    }
  };

  const handleImageChange = (e) => {
    const files = Array.from(e.target.files);

    // Check max images limit
    if (formData.images.length + files.length > 4) {
      alert("You can only upload up to 4 images per product.");
      return;
    }

    // Check file size limit (5MB)
    const MAX_SIZE = 5 * 1024 * 1024; // 5MB
    const oversizedFiles = files.filter((file) => file.size > MAX_SIZE);

    if (oversizedFiles.length > 0) {
      alert(
        `Some files are too large! Please upload images smaller than 5MB. (${oversizedFiles.length} file(s) skipped)`,
      );
      return;
    }

    const newImages = [...formData.images, ...files];
    setFormData({ ...formData, images: newImages });

    // Create previews
    const newPreviews = files.map((file) => URL.createObjectURL(file));
    setPreviews([...previews, ...newPreviews]);
  };

  const removeImage = (index) => {
    const newImages = formData.images.filter((_, i) => i !== index);
    const newPreviews = previews.filter((_, i) => i !== index);
    setFormData({ ...formData, images: newImages });
    setPreviews(newPreviews);
  };

  const handleSizeToggle = (size) => {
    setFormData((prev) => ({
      ...prev,
      sizes: prev.sizes.includes(size)
        ? prev.sizes.filter((s) => s !== size)
        : [...prev.sizes, size],
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const token = localStorage.getItem("token");

    if (activeTab === "workComponents") {
      const items = formData.workItems || [];
      const baseId = formData.compatibleBases?.[0];

      if (!baseId) return alert("Please select a blouse to map these works to");
      if (items.length === 0) return alert("Please add at least one work item");

      // Check if all items have images
      const missingImage = items.find((item) => !item.image);
      if (missingImage)
        return alert(
          `Please upload an image for "${missingImage.name || "all work items"}"`,
        );

      try {
        for (const item of items) {
          const itemData = new FormData();
          itemData.append("name", item.name);
          itemData.append("type", item.workType);
          itemData.append("additionalPrice", item.additionalPrice || 0);
          itemData.append("compatibleBases", baseId);
          itemData.append("images", item.image);

          const res = await fetch(
            `${API_BASE_URL}/api/customization/components`,
            {
              method: "POST",
              headers: { Authorization: `Bearer ${token}` },
              body: itemData,
            },
          );
          if (!res.ok) throw new Error(`Failed to upload ${item.name}`);
        }
        alert("All work items uploaded successfully!");
        setShowUpload(false);
        fetchCustomizationData();
        return;
      } catch (err) {
        alert(err.message);
        return;
      }
    }

    if (formData.images.length === 0) {
      alert("Please upload at least one image.");
      return;
    }

    const data = new FormData();
    let endpoint = `${API_BASE_URL}/api/products`;

    if (activeTab === "blouseBases") {
      endpoint = `${API_BASE_URL}/api/customization/bases`;
      data.append("name", formData.name);
      data.append("brand", "Below2000");
      data.append("fabricType", formData.fabric);
      data.append("neckType", formData.neckType || "Round");
      data.append("sleeveType", formData.sleeveType || "Short");
      data.append("basePrice", formData.price);
      // Construct colors array for backend
      const colorData = [
        {
          colorName: formData.colors || "Pink",
          images: [], // images will be handled by multer
        },
      ];
      data.append("colors", JSON.stringify(colorData));
    } else if (activeTab === "references") {
      endpoint = `${API_BASE_URL}/api/customization/references`;
      data.append("name", formData.name);
      data.append("baseBlouse", formData.baseBlouseId);
      data.append("description", "Real stitched reference");
    } else {
      // Regular product
      data.append("name", formData.name);
      data.append("brand", formData.brand);
      data.append("price", formData.price);
      data.append("subCategory", formData.subCategory);
      data.append("description", formData.description);
      data.append("fabric", formData.fabric);
      data.append("colors", formData.colors);
      data.append("sizes", formData.sizes.join(","));
    }

    // Append all image files
    formData.images.forEach((image) => {
      data.append("images", image);
    });

    try {
      const response = await fetch(endpoint, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
        },
        body: data,
      });

      if (response.ok) {
        alert("Items published successfully!");
        setShowUpload(false);
        if (activeTab === "products") fetchProducts();
        else fetchCustomizationData();
      } else {
        const errorData = await response.json();
        const errorMessage = errorData.message || "Unknown error";
        const errorDetail =
          errorData.error ||
          (errorData.details ? JSON.stringify(errorData.details) : "");
        alert(`Failed to publish: ${errorMessage}\nDetail: ${errorDetail}`);
      }
    } catch (err) {
      console.error(err);
      alert("Error connecting to server");
    }
  };

  const openUploadModal = () => {
    fetchCategories();
    setFormData({
      name: "",
      brand: "",
      price: "",
      subCategory: categories.length > 0 ? categories[0].name : "",
      description: "",
      sizes: [],
      colors: "",
      fabric: "",
      images: [],
      neckType: "",
      sleeveType: "",
      workType: "",
      compatibleNeckTypes: "",
      compatibleSleeveTypes: "",
      compatibleBases: [],
      additionalPrice: 0,
      baseBlouseId: "",
    });
    setPreviews([]);
    setShowUpload(true);
  };

  const selectedBaseModel =
    activeTab === "workComponents"
      ? blouseBases.find((b) => b._id === formData.compatibleBases?.[0])
      : null;

  return (
    <div className="container dashboard">
      <div className="dashboard-header">
        <h1>Vendor Dashboard</h1>
        <div className="dashboard-header-btns">
          <button className="logout-btn-dashboard" onClick={onLogout}>
            Logout
          </button>
          <button className="add-btn" onClick={openUploadModal}>
            + Add{" "}
            {activeTab === "products" ? "Product" : activeTab.slice(0, -1)}
          </button>
        </div>
      </div>

      <div className="dashboard-tabs">
        <button
          className={activeTab === "products" ? "active" : ""}
          onClick={() => setActiveTab("products")}
        >
          Regular Products
        </button>
        <button
          className={activeTab === "blouseBases" ? "active" : ""}
          onClick={() => setActiveTab("blouseBases")}
        >
          Blouse Bases
        </button>
        <button
          className={activeTab === "workComponents" ? "active" : ""}
          onClick={() => setActiveTab("workComponents")}
        >
          Work Components
        </button>
        <button
          className={activeTab === "references" ? "active" : ""}
          onClick={() => setActiveTab("references")}
        >
          Reference / Real Blouses
        </button>
      </div>

      <div className="stats-row">
        <div className="stat-card">
          <h3>12</h3>
          <p>Active Listings</p>
        </div>
        <div className="stat-card">
          <h3>45</h3>
          <p>Orders this month</p>
        </div>
        <div className="stat-card">
          <h3>₹24,500</h3>
          <p>Total Earnings</p>
        </div>
      </div>

      {showUpload && (
        <div className="modal">
          <div className="modal-content">
            <h2>
              {activeTab === "products" && "Add New Product"}
              {activeTab === "blouseBases" && "Add Blouse Base (Plain)"}
              {activeTab === "workComponents" && "Add Work Component"}
              {activeTab === "references" && "Add Finished/Real Blouse"}
            </h2>

            <form onSubmit={handleSubmit}>
              {activeTab === "products" ? (
                <>
                  <div className="form-group">
                    <label>Product Name</label>
                    <input
                      type="text"
                      required
                      onChange={(e) =>
                        setFormData({ ...formData, name: e.target.value })
                      }
                    />
                  </div>
                  <div className="form-grid">
                    <div className="form-group">
                      <label>Brand</label>
                      <input
                        type="text"
                        required
                        onChange={(e) =>
                          setFormData({ ...formData, brand: e.target.value })
                        }
                      />
                    </div>
                    <div className="form-group">
                      <label>Price (Max ₹2000)</label>
                      <input
                        type="number"
                        max="2000"
                        required
                        onChange={(e) =>
                          setFormData({ ...formData, price: e.target.value })
                        }
                      />
                    </div>
                  </div>
                  <div className="form-group">
                    <label>Category Type</label>
                    <div className="category-input-group">
                      <select
                        value={formData.subCategory}
                        onChange={(e) => {
                          if (e.target.value === "ADD_NEW") {
                            setShowAddCategory(true);
                          } else {
                            setFormData({
                              ...formData,
                              subCategory: e.target.value,
                            });
                          }
                        }}
                      >
                        <option value="">Select Category</option>
                        {categories.map((c) => (
                          <option key={c._id} value={c.name}>
                            {c.name}
                          </option>
                        ))}
                        <option value="ADD_NEW">+ Add New Category</option>
                      </select>
                    </div>

                    {showAddCategory && (
                      <div className="add-category-inline">
                        <input
                          type="text"
                          placeholder="Enter new category"
                          value={newCategoryName}
                          onChange={(e) => setNewCategoryName(e.target.value)}
                        />
                        <button type="button" onClick={handleAddCategory}>
                          Add
                        </button>
                        <button
                          type="button"
                          className="cancel-btn"
                          onClick={() => setShowAddCategory(false)}
                        >
                          Cancel
                        </button>
                      </div>
                    )}
                  </div>
                  <div className="form-group">
                    <label>Available Sizes</label>
                    <div className="size-chips">
                      {availableSizes.map((size) => (
                        <div
                          key={size}
                          className={`chip ${formData.sizes.includes(size) ? "active" : ""}`}
                          onClick={() => handleSizeToggle(size)}
                        >
                          {size}
                        </div>
                      ))}
                    </div>
                  </div>
                  <div className="form-group">
                    <label>Product Images (Up to 4)</label>
                    <div className="previews-grid">
                      {previews.map((p, i) => (
                        <div key={i} className="preview-item">
                          <img src={p} alt="" />
                          <button onClick={() => removeImage(i)}>×</button>
                        </div>
                      ))}
                      {previews.length < 4 && (
                        <label className="upload-placeholder">
                          <input
                            type="file"
                            multiple
                            accept="image/*"
                            onChange={handleImageChange}
                            hidden
                          />
                          <span>+ Add</span>
                        </label>
                      )}
                    </div>
                  </div>
                </>
              ) : activeTab === "blouseBases" ? (
                <>
                  <div className="form-group">
                    <label>Model Name</label>
                    <input
                      type="text"
                      required
                      placeholder="e.g. Elegant Round Base"
                      onChange={(e) =>
                        setFormData({ ...formData, name: e.target.value })
                      }
                    />
                  </div>
                  <div className="form-grid">
                    <div className="form-group">
                      <label>Neck Type</label>
                      <input
                        type="text"
                        placeholder="e.g. Round, V-Neck, Deep Neck"
                        required
                        onChange={(e) =>
                          setFormData({ ...formData, neckType: e.target.value })
                        }
                      />
                    </div>
                    <div className="form-group">
                      <label>Sleeve Type</label>
                      <input
                        type="text"
                        placeholder="e.g. Short, Sleeveless, 3/4"
                        required
                        onChange={(e) =>
                          setFormData({
                            ...formData,
                            sleeveType: e.target.value,
                          })
                        }
                      />
                    </div>
                  </div>
                  <div className="form-grid">
                    <div className="form-group">
                      <label>Fabric Type</label>
                      <input
                        type="text"
                        placeholder="Cotton, Silk"
                        onChange={(e) =>
                          setFormData({ ...formData, fabric: e.target.value })
                        }
                      />
                    </div>
                    <div className="form-group">
                      <label>Base Price</label>
                      <input
                        type="number"
                        required
                        onChange={(e) =>
                          setFormData({ ...formData, price: e.target.value })
                        }
                      />
                    </div>
                  </div>
                  <div className="form-group">
                    <label>Blouse Color & Images (Front/Back)</label>
                    <input
                      type="text"
                      placeholder="Pink"
                      onChange={(e) =>
                        setFormData({ ...formData, colors: e.target.value })
                      }
                    />
                    <div
                      className="previews-grid"
                      style={{ marginTop: "10px" }}
                    >
                      {previews.map((p, i) => (
                        <div key={i} className="preview-item">
                          <img src={p} alt="" />
                        </div>
                      ))}
                      <label className="upload-placeholder">
                        <input
                          type="file"
                          multiple
                          accept="image/*"
                          onChange={handleImageChange}
                          hidden
                        />
                        <span>+ Add Images</span>
                      </label>
                    </div>
                  </div>
                </>
              ) : activeTab === "workComponents" ? (
                <>
                  <div className="mapping-header-section">
                    <label>1. Select Blouse to Map All Work To</label>
                    <select
                      className="form-select dropdown-highlight"
                      required
                      value={formData.compatibleBases?.[0] || ""}
                      onChange={(e) => {
                        const val = e.target.value;
                        setFormData({
                          ...formData,
                          compatibleBases: val ? [val] : [],
                        });
                      }}
                    >
                      <option value="">-- Select Your Blouse Model --</option>
                      {blouseBases.map((b) => (
                        <option key={b._id} value={b._id}>
                          {b.name}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div className="work-items-container">
                    <label>
                      2. Add Work Details (Neck, Sleeve, Back, etc.)
                    </label>
                    <div className="work-items-list">
                      {(formData.workItems || [{ id: Date.now() }]).map(
                        (item, index) => (
                          <div key={item.id} className="work-item-entry">
                            <div className="item-header">
                              <h4>Work Item #{index + 1}</h4>
                              {index > 0 && (
                                <button
                                  type="button"
                                  className="remove-item-btn"
                                  onClick={() => {
                                    const newItems = formData.workItems.filter(
                                      (_, i) => i !== index,
                                    );
                                    setFormData({
                                      ...formData,
                                      workItems: newItems,
                                    });
                                  }}
                                >
                                  Remove
                                </button>
                              )}
                            </div>

                            <div className="form-grid">
                              <div className="form-group">
                                <label>Work Name</label>
                                <input
                                  type="text"
                                  placeholder="e.g. Front Neck Embroidery"
                                  value={item.name || ""}
                                  onChange={(e) => {
                                    const newItems = [
                                      ...(formData.workItems || [
                                        { id: item.id },
                                      ]),
                                    ];
                                    newItems[index] = {
                                      ...newItems[index],
                                      name: e.target.value,
                                    };
                                    setFormData({
                                      ...formData,
                                      workItems: newItems,
                                    });
                                  }}
                                />
                              </div>
                              <div className="form-group">
                                <label>Category (Type)</label>
                                <input
                                  type="text"
                                  placeholder="Neck, Sleeve, etc."
                                  value={item.workType || ""}
                                  onChange={(e) => {
                                    const newItems = [
                                      ...(formData.workItems || [
                                        { id: item.id },
                                      ]),
                                    ];
                                    newItems[index] = {
                                      ...newItems[index],
                                      workType: e.target.value,
                                    };
                                    setFormData({
                                      ...formData,
                                      workItems: newItems,
                                    });
                                  }}
                                />
                              </div>
                            </div>

                            <div className="form-grid">
                              <div className="form-group">
                                <label>Work Rate (₹)</label>
                                <input
                                  type="number"
                                  placeholder="0"
                                  value={item.additionalPrice || ""}
                                  onChange={(e) => {
                                    const newItems = [
                                      ...(formData.workItems || [
                                        { id: item.id },
                                      ]),
                                    ];
                                    newItems[index] = {
                                      ...newItems[index],
                                      additionalPrice: e.target.value,
                                    };
                                    setFormData({
                                      ...formData,
                                      workItems: newItems,
                                    });
                                  }}
                                />
                              </div>
                              <div className="form-group">
                                <label>Image (Transparent PNG)</label>
                                <div className="item-image-upload">
                                  {item.preview ? (
                                    <div className="item-preview">
                                      <img src={item.preview} alt="" />
                                      <button
                                        type="button"
                                        onClick={() => {
                                          const newItems = [
                                            ...(formData.workItems || []),
                                          ];
                                          newItems[index] = {
                                            ...newItems[index],
                                            preview: null,
                                            image: null,
                                          };
                                          setFormData({
                                            ...formData,
                                            workItems: newItems,
                                          });
                                        }}
                                      >
                                        ×
                                      </button>
                                    </div>
                                  ) : (
                                    <label className="item-upload-trigger">
                                      <input
                                        type="file"
                                        hidden
                                        onChange={(e) => {
                                          const file = e.target.files[0];
                                          if (file) {
                                            const reader = new FileReader();
                                            reader.onload = () => {
                                              const newItems = [
                                                ...(formData.workItems || [
                                                  { id: item.id },
                                                ]),
                                              ];
                                              newItems[index] = {
                                                ...newItems[index],
                                                preview: reader.result,
                                                image: file,
                                              };
                                              setFormData({
                                                ...formData,
                                                workItems: newItems,
                                              });
                                            };
                                            reader.readAsDataURL(file);
                                          }
                                        }}
                                      />
                                      <span>+ Add Image</span>
                                    </label>
                                  )}
                                </div>
                              </div>
                            </div>
                          </div>
                        ),
                      )}
                    </div>

                    <button
                      type="button"
                      className="add-another-item-btn"
                      onClick={() => {
                        const newItems = [
                          ...(formData.workItems || [{ id: Date.now() - 1 }]),
                          { id: Date.now() },
                        ];
                        setFormData({ ...formData, workItems: newItems });
                      }}
                    >
                      + Add Another Work (Back/Sleeve/etc.)
                    </button>
                  </div>
                </>
              ) : (
                <>
                  <div className="form-group">
                    <label>Reference Name</label>
                    <input
                      type="text"
                      required
                      placeholder="e.g. Finished Party Wear Blouse"
                      onChange={(e) =>
                        setFormData({ ...formData, name: e.target.value })
                      }
                    />
                  </div>
                  <div className="form-group">
                    <label>Base Blouse Model</label>
                    <select
                      onChange={(e) =>
                        setFormData({
                          ...formData,
                          baseBlouseId: e.target.value,
                        })
                      }
                    >
                      <option value="">Select Base</option>
                      {blouseBases.map((b) => (
                        <option key={b._id} value={b._id}>
                          {b.name}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div className="form-group">
                    <label>Real Stitched Images</label>
                    <div className="previews-grid">
                      {previews.map((p, i) => (
                        <div key={i} className="preview-item">
                          <img src={p} alt="" />
                        </div>
                      ))}
                      <label className="upload-placeholder">
                        <input
                          type="file"
                          multiple
                          accept="image/*"
                          onChange={handleImageChange}
                          hidden
                        />
                        <span>+ Add Images</span>
                      </label>
                    </div>
                  </div>
                </>
              )}

              <div className="modal-actions">
                <button
                  type="button"
                  className="cancel-link"
                  onClick={() => setShowUpload(false)}
                >
                  Cancel
                </button>
                <button type="submit" className="submit-btn">
                  Publish {activeTab.slice(0, -1)}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {activeTab === "products" && (
        <div className="product-list">
          <h2>Your Listings</h2>
          <table>
            <thead>
              <tr>
                <th>Image</th>
                <th>Product Details</th>
                <th>Category</th>
                <th>Price</th>
                <th>Stock</th>
                <th>Status</th>
              </tr>
            </thead>
            <tbody>
              {products.length > 0 ? (
                products.map((p) => (
                  <tr key={p._id}>
                    <td>
                      <img
                        src={
                          p.images?.[0]
                            ? p.images[0].startsWith("http")
                              ? p.images[0]
                              : `${API_BASE_URL}${p.images[0]}`
                            : "https://via.placeholder.com/50"
                        }
                        alt=""
                        className="table-img"
                      />
                    </td>
                    <td>
                      <div className="td-name">{p.name}</div>
                      <div className="td-brand">{p.brand}</div>
                    </td>
                    <td>{p.subCategory}</td>
                    <td>₹{p.price}</td>
                    <td>24</td>
                    <td>
                      <span className="status-badge">Active</span>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan="6">No products found. Start by uploading!</td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}

      {activeTab === "blouseBases" && (
        <div className="blouse-visual-gallery">
          <div className="gallery-header">
            <h2>Your Blouse Collection</h2>
            <p>
              Select any blouse to see its options and mapped embroidery works.
            </p>
          </div>

          <div className="blouse-cards-grid">
            {blouseBases.map((b) => (
              <div key={b._id} className="blouse-gallery-card">
                <div className="card-image-main">
                  <img
                    src={
                      b.colors[0]?.images[0]
                        ? b.colors[0].images[0].startsWith("http")
                          ? b.colors[0].images[0]
                          : `${API_BASE_URL}${b.colors[0].images[0]}`
                        : ""
                    }
                    alt={b.name}
                  />
                  <div className="color-count-tag">
                    {b.colors.length} Colors
                  </div>
                </div>
                <div className="card-body">
                  <h3 className="card-title">{b.name}</h3>
                  <div className="card-specs">
                    {b.neckType} | {b.sleeveType}
                  </div>
                  <div className="card-action-row">
                    <span className="card-price">₹{b.basePrice}</span>
                    <button
                      className="manage-btn"
                      onClick={() => setSelectedMappingBlouse(b)}
                    >
                      Manage Details
                    </button>
                  </div>
                </div>
              </div>
            ))}
            {blouseBases.length === 0 && (
              <div className="empty-gallery">
                <div className="empty-icon">🧥</div>
                <h3>No Blouses Yet</h3>
                <p>Start by clicking the "Add Blouse Base" button above.</p>
              </div>
            )}
          </div>

          {selectedMappingBlouse && (
            <div
              className="mapping-overlay-modal"
              onClick={() => setSelectedMappingBlouse(null)}
            >
              <div
                className="overlay-modal-content"
                onClick={(e) => e.stopPropagation()}
              >
                <button
                  className="close-overlay"
                  onClick={() => setSelectedMappingBlouse(null)}
                >
                  ×
                </button>
                <div className="overlay-flex">
                  <div className="overlay-preview-image">
                    <img
                      src={
                        selectedMappingBlouse.colors[0]?.images[0]
                          ? selectedMappingBlouse.colors[0].images[0].startsWith(
                              "http",
                            )
                            ? selectedMappingBlouse.colors[0].images[0]
                            : `${API_BASE_URL}${selectedMappingBlouse.colors[0].images[0]}`
                          : ""
                      }
                      alt=""
                    />
                  </div>
                  <div className="overlay-details">
                    <h2>{selectedMappingBlouse.name}</h2>
                    <div className="price-badge">
                      Starting from ₹{selectedMappingBlouse.basePrice}
                    </div>

                    <div className="mapping-section">
                      <label>Color Variations</label>
                      <div className="color-swatches">
                        {selectedMappingBlouse.colors.map((c) => (
                          <div key={c.colorName} className="swatch-wrapper">
                            <div
                              className="swatch-circle"
                              style={{ background: c.colorName.toLowerCase() }}
                            ></div>
                            <span>{c.colorName}</span>
                          </div>
                        ))}
                      </div>
                    </div>

                    <div className="mapping-section">
                      <label>Mapped Embroidery Works</label>
                      <div className="works-mini-gallery">
                        {workComponents.filter((c) =>
                          c.compatibleBases.includes(selectedMappingBlouse._id),
                        ).length > 0 ? (
                          workComponents
                            .filter((c) =>
                              c.compatibleBases.includes(
                                selectedMappingBlouse._id,
                              ),
                            )
                            .map((comp) => (
                              <div key={comp._id} className="work-mini-item">
                                <img
                                  src={`${API_BASE_URL}${comp.images[0]}`}
                                  alt=""
                                />
                                <div className="work-meta">
                                  <strong>{comp.name}</strong>
                                  <span>+₹{comp.additionalPrice}</span>
                                </div>
                              </div>
                            ))
                        ) : (
                          <div className="no-works-tag">
                            No work components mapped yet.
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {activeTab === "workComponents" && (
        <div className="product-list">
          <h2>Work Components</h2>
          <table>
            <thead>
              <tr>
                <th>Image</th>
                <th>Component Name</th>
                <th>Type</th>
                <th>Price</th>
                <th>Compatibility</th>
              </tr>
            </thead>
            <tbody>
              {workComponents.map((c) => (
                <tr key={c._id}>
                  <td>
                    <img
                      src={`${API_BASE_URL}${c.images[0]}`}
                      className="table-img"
                      alt=""
                    />
                  </td>
                  <td>{c.name}</td>
                  <td>{c.type}</td>
                  <td>+₹{c.additionalPrice}</td>
                  <td style={{ fontSize: "11px" }}>
                    {c.compatibleNeckTypes.join(", ")}
                  </td>
                </tr>
              ))}
              {workComponents.length === 0 && (
                <tr>
                  <td colSpan="5">No components found.</td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}

      {activeTab === "references" && (
        <div className="product-list">
          <h2>Reference / Real Blouses</h2>
          <table>
            <thead>
              <tr>
                <th>Image</th>
                <th>Name</th>
                <th>Base Blouse</th>
                <th>Components Used</th>
              </tr>
            </thead>
            <tbody>
              {references.map((r) => (
                <tr key={r._id}>
                  <td>
                    <img
                      src={`${API_BASE_URL}${r.images[0]}`}
                      className="table-img"
                      alt=""
                    />
                  </td>
                  <td>{r.name}</td>
                  <td>{r.baseBlouse?.name || "-"}</td>
                  <td style={{ fontSize: "11px" }}>
                    {r.workComponents.map((wc) => wc.name).join(", ")}
                  </td>
                </tr>
              ))}
              {references.length === 0 && (
                <tr>
                  <td colSpan="4">No reference blouses found.</td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default VendorDashboard;
